import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import api from '../api';
import { LOCATION_TREE } from '../data/locations';
import './Settings.css';

const DEFAULT_NOTIFICATION_PREFERENCES = {
  task: true,
  bid: true,
  reply: true,
  review: true,
  announcement: true,
};

const PHONE_VERIFICATION_ENABLED = process.env.REACT_APP_PHONE_VERIFICATION_ENABLED === 'true';

function normalizeNotificationPreferences(value) {
  if (!value || typeof value !== 'object') return { ...DEFAULT_NOTIFICATION_PREFERENCES };
  return Object.fromEntries(
    Object.keys(DEFAULT_NOTIFICATION_PREFERENCES).map(key => [
      key,
      typeof value[key] === 'boolean' ? value[key] : DEFAULT_NOTIFICATION_PREFERENCES[key],
    ])
  );
}

export default function Settings() {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const text = language === 'zh' ? {
    title: '⚙️ 账户设置',
    tabs: { account: '👤 账户', password: '🔒 密码', notif: '🔔 通知', danger: '⚠️ 危险区域' },
    accountInfo: '账户信息',
    username: '用户名',
    email: '邮箱',
    country: '国家',
    city: '城市',
    district: '区域',
    phone: '手机号',
    phoneHint: '请输入带国家区号的手机号，例如 +15551234567',
    phoneVerified: '已验证',
    phoneUnverified: '未验证',
    verificationCode: '验证码',
    sendPhoneCode: '发送验证码',
    confirmPhoneCode: '确认手机号',
    phoneCodeSent: '验证码已发送',
    phoneVerifiedSaved: '手机号已验证',
    phoneVerificationFailed: '手机号验证失败',
    bio: '个人简介',
    avatar: '头像',
    uploadAvatar: '上传头像',
    avatarUploading: '上传中...',
    avatarUploaded: '头像已更新',
    avatarFailed: '头像上传失败',
    save: '保存修改',
    saving: '保存中...',
    accountSaved: '账户信息已保存',
    accountFailed: '账户信息保存失败',
    passwordTitle: '修改密码',
    currentPassword: '当前密码',
    newPassword: '新密码',
    confirmPassword: '确认新密码',
    updatePassword: '更新密码',
    updating: '更新中...',
    fillAll: '请填写所有字段',
    mismatch: '两次密码不一致',
    passwordLength: '密码至少 8 位',
    passwordSaved: '密码已更新',
    passwordFailed: '密码更新失败',
    notifTitle: '通知偏好',
    notificationLanguage: '通知语言',
    notificationLanguageDesc: '系统通知标题和正文优先使用此语言显示',
    notificationLanguageOptions: { en: 'English', zh: '中文' },
    prefsSaved: '偏好已保存',
    savePrefs: '保存偏好',
    clearConfirm: '清除所有本地缓存数据吗？',
    cleared: '本地数据已清空，刷新后生效',
    confirmClear: '确认清空',
    cancel: '取消',
    dangerTitle: '⚠️ 危险区域',
    clearTitle: '清空本地缓存数据',
    clearDesc: '删除本地 localStorage 的缓存数据（消息安全确认、旧版偏好缓存等），账户本身不会被删除。',
    clearNow: '立即清空',
    prefs: [
      { key: 'task', label: '需求更新', desc: '关注的需求状态变化时提醒' },
      { key: 'bid', label: '新报价', desc: '收到新报价时提醒' },
      { key: 'reply', label: '论坛回复', desc: '你的帖子有新回复时提醒' },
      { key: 'review', label: '新评价', desc: '收到新评价时提醒' },
      { key: 'announcement', label: '平台公告', desc: '接收系统公告' },
    ],
  } : {
    title: '⚙️ Account settings',
    tabs: { account: '👤 Account', password: '🔒 Password', notif: '🔔 Notifications', danger: '⚠️ Danger zone' },
    accountInfo: 'Account information',
    username: 'Username',
    email: 'Email',
    country: 'Country',
    city: 'City',
    district: 'District',
    phone: 'Phone number',
    phoneHint: 'Use international format, for example +15551234567',
    phoneVerified: 'Verified',
    phoneUnverified: 'Unverified',
    verificationCode: 'Verification code',
    sendPhoneCode: 'Send code',
    confirmPhoneCode: 'Confirm phone',
    phoneCodeSent: 'Verification code sent',
    phoneVerifiedSaved: 'Phone number verified',
    phoneVerificationFailed: 'Phone verification failed',
    phoneVerificationPaused: 'Phone verification is currently paused',
    bio: 'Bio',
    avatar: 'Avatar',
    uploadAvatar: 'Upload avatar',
    avatarUploading: 'Uploading...',
    avatarUploaded: 'Avatar updated',
    avatarFailed: 'Failed to upload avatar',
    save: 'Save changes',
    saving: 'Saving...',
    accountSaved: 'Account information saved',
    accountFailed: 'Failed to save account information',
    passwordTitle: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    updatePassword: 'Update password',
    updating: 'Updating...',
    fillAll: 'Please fill in all fields',
    mismatch: 'Passwords do not match',
    passwordLength: 'Password must be at least 8 characters',
    passwordSaved: 'Password updated',
    passwordFailed: 'Failed to update password',
    notifTitle: 'Notification preferences',
    notificationLanguage: 'Notification language',
    notificationLanguageDesc: 'System notification titles and content prefer this language',
    notificationLanguageOptions: { en: 'English', zh: '中文' },
    prefsSaved: 'Preferences saved',
    savePrefs: 'Save preferences',
    clearConfirm: 'Clear all local cached data?',
    cleared: 'Local data cleared. Refresh to apply.',
    confirmClear: 'Confirm clear',
    cancel: 'Cancel',
    dangerTitle: '⚠️ Danger zone',
    clearTitle: 'Clear local cached data',
    clearDesc: 'Delete localStorage cache such as message safety acknowledgements and legacy preference cache. Your account will not be deleted.',
    clearNow: 'Clear now',
    prefs: [
      { key: 'task', label: 'Requirement updates', desc: 'Notify me when followed requirements change status' },
      { key: 'bid', label: 'New quotes', desc: 'Notify me when I receive new quotes' },
      { key: 'reply', label: 'Forum replies', desc: 'Notify me when my post receives a reply' },
      { key: 'review', label: 'New reviews', desc: 'Notify me when I receive a review' },
      { key: 'announcement', label: 'Platform announcements', desc: 'Receive system announcements' },
    ],
  };
  const [tab, setTab] = useState('account');

  const [account, setAccount] = useState({
    username: user?.username || '',
    email: user?.email || '',
    bio: user?.bio || '',
    country: user?.country || '',
    city: user?.city || '',
    district: user?.district || '',
  });
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [phoneCode, setPhoneCode] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar || '');
  const [notificationLocale, setNotificationLocale] = useState(user?.notificationLocale || language || 'en');

  const [pwd, setPwd] = useState({ current: '', newPwd: '', confirm: '' });
  const [prefs, setPrefs] = useState(() => normalizeNotificationPreferences(user?.notificationPreferences));
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [clearConfirmVisible, setClearConfirmVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const phoneVerificationPaused = text.phoneVerificationPaused || 'Phone verification is currently paused';

  const showSaved = (message) => {
    setSaved(message);
    setTimeout(() => setSaved(''), 2200);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 3200);
  };

  const saveAccount = async () => {
    setError('');
    setSaving(true);
    try {
      const res = await api.put('/users/me', {
        username: account.username,
        email: account.email,
        bio: account.bio,
        country: account.country,
        city: account.city,
        district: account.district,
        skills: user?.skills || [],
          avatar: avatarUrl,
          notificationLocale: notificationLocale,
          notificationPreferences: prefs,
        });
      updateUser(res.data.user);
      showSaved(text.accountSaved);
    } catch (err) {
      showError(err.response?.data?.error || text.accountFailed);
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setError('');
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.append('purpose', 'avatar');
      form.append('file', file);
      const upload = await api.post('/uploads', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const nextAvatar = upload.data.asset.url;
      setAvatarUrl(nextAvatar);
      const res = await api.put('/users/me', {
        username: account.username,
        email: account.email,
        bio: account.bio,
        country: account.country,
        city: account.city,
        district: account.district,
        skills: user?.skills || [],
        avatar: nextAvatar,
        notificationLocale: notificationLocale,
        notificationPreferences: prefs,
      });
      updateUser(res.data.user);
      showSaved(text.avatarUploaded);
    } catch (err) {
      showError(err.response?.data?.error || text.avatarFailed);
    } finally {
      setAvatarUploading(false);
    }
  };

  const sendPhoneCode = async () => {
    if (!PHONE_VERIFICATION_ENABLED) {
      showError(phoneVerificationPaused);
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api.post('/users/me/phone/start-verification', { phoneNumber });
      showSaved(text.phoneCodeSent);
    } catch (err) {
      showError(err.response?.data?.error || text.phoneVerificationFailed);
    } finally {
      setSaving(false);
    }
  };

  const confirmPhoneCode = async () => {
    if (!PHONE_VERIFICATION_ENABLED) {
      showError(phoneVerificationPaused);
      return;
    }
    setError('');
    setSaving(true);
    try {
      const res = await api.post('/users/me/phone/confirm-verification', { phoneNumber, code: phoneCode });
      updateUser(res.data.user);
      setPhoneCode('');
      showSaved(text.phoneVerifiedSaved);
    } catch (err) {
      showError(err.response?.data?.error || text.phoneVerificationFailed);
    } finally {
      setSaving(false);
    }
  };

  const savePwd = async () => {
    setError('');
    if (!pwd.current || !pwd.newPwd) { showError(text.fillAll); return; }
    if (pwd.newPwd !== pwd.confirm) { showError(text.mismatch); return; }
    if (pwd.newPwd.length < 8) { showError(text.passwordLength); return; }
    setSaving(true);
    try {
      await api.put('/users/me/password', {
        currentPassword: pwd.current,
        newPassword: pwd.newPwd,
      });
      setPwd({ current: '', newPwd: '', confirm: '' });
      showSaved(text.passwordSaved);
    } catch (err) {
      showError(err.response?.data?.error || text.passwordFailed);
    } finally {
      setSaving(false);
    }
  };

  const savePrefs = () => {
    setSaving(true);
    api.put('/users/me', {
      username: account.username,
      email: account.email,
      bio: account.bio,
      country: account.country,
      city: account.city,
      district: account.district,
      skills: user?.skills || [],
      avatar: avatarUrl,
      notificationLocale: notificationLocale,
      notificationPreferences: prefs,
    }).then(res => {
      updateUser(res.data.user);
      showSaved(text.prefsSaved);
    }).catch(err => {
      showError(err.response?.data?.error || text.accountFailed);
    }).finally(() => setSaving(false));
  };

  const clearData = () => {
    setClearConfirmVisible(false);
    ['rc_messages_contacts', 'rc_messages_map', 'settings_prefs'].forEach(k => localStorage.removeItem(k));
    showSaved(text.cleared);
  };

  return (
    <div className="sg-page">
      <div className="container">
        <h1>{text.title}</h1>

        <div className="sg-layout">
          <aside className="sg-sidebar">
            <button className={tab === 'account' ? 'active' : ''} onClick={() => setTab('account')}>{text.tabs.account}</button>
            <button className={tab === 'password' ? 'active' : ''} onClick={() => setTab('password')}>{text.tabs.password}</button>
            <button className={tab === 'notif' ? 'active' : ''} onClick={() => setTab('notif')}>{text.tabs.notif}</button>
            <button className={tab === 'danger' ? 'active' : ''} onClick={() => setTab('danger')}>{text.tabs.danger}</button>
          </aside>

          <main className="sg-main">
            {saved && <div className="sg-toast">{saved}</div>}
            {error && <div className="sg-toast sg-toast-error">{error}</div>}

            {tab === 'account' && (
              <section className="sg-card">
                <h2>{text.accountInfo}</h2>
                <label>{text.avatar}</label>
                <div className="sg-avatar-row">
                  <div className="sg-avatar-preview">
                    {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{account.username?.[0]?.toUpperCase() || 'U'}</span>}
                  </div>
                  <label className="btn btn-secondary sg-avatar-upload">
                    {avatarUploading ? text.avatarUploading : text.uploadAvatar}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={e => uploadAvatar(e.target.files?.[0])}
                      disabled={avatarUploading}
                    />
                  </label>
                </div>
                <label>{text.username}</label>
                <input type="text" value={account.username} onChange={e => setAccount({ ...account, username: e.target.value })} />
                <label>{text.email}</label>
                <input type="email" value={account.email} onChange={e => setAccount({ ...account, email: e.target.value })} />
                <div className="sg-grid-3">
                  <label>{text.country}
                    <select value={account.country} onChange={e => setAccount({ ...account, country: e.target.value, city: '', district: '' })}>
                      <option value="">{text.country}</option>
                      {LOCATION_TREE.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                    </select>
                  </label>
                  <label>{text.city}
                    <select value={account.city} disabled={!account.country} onChange={e => setAccount({ ...account, city: e.target.value, district: '' })}>
                      <option value="">{text.city}</option>
                      {(LOCATION_TREE.find(item => item.value === account.country)?.cities || []).map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                    </select>
                  </label>
                  <label>{text.district}
                    <select value={account.district} disabled={!account.city} onChange={e => setAccount({ ...account, district: e.target.value })}>
                      <option value="">{text.district}</option>
                      {(LOCATION_TREE.find(item => item.value === account.country)?.cities.find(item => item.value === account.city)?.districts || []).map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                    </select>
                  </label>
                </div>
                <label>{text.phone}</label>
                <div className="sg-phone-row">
                  <input
                    type="tel"
                    value={phoneNumber}
                    placeholder={text.phoneHint}
                    onChange={e => setPhoneNumber(e.target.value)}
                  />
                  <span className={`sg-phone-status ${user?.phoneVerified ? 'verified' : ''}`}>
                    {user?.phoneVerified ? text.phoneVerified : text.phoneUnverified}
                  </span>
                </div>
                {PHONE_VERIFICATION_ENABLED && (
                  <div className="sg-code-row">
                    <button className="btn btn-secondary" type="button" onClick={sendPhoneCode} disabled={saving || !phoneNumber}>
                      {text.sendPhoneCode}
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength="6"
                      value={phoneCode}
                      placeholder={text.verificationCode}
                      onChange={e => setPhoneCode(e.target.value)}
                    />
                    <button className="btn btn-primary" type="button" onClick={confirmPhoneCode} disabled={saving || !phoneNumber || !phoneCode}>
                      {text.confirmPhoneCode}
                    </button>
                  </div>
                )}
                <label>{text.bio}</label>
                <textarea rows="3" value={account.bio} onChange={e => setAccount({ ...account, bio: e.target.value })}></textarea>
                <button className="btn btn-primary" onClick={saveAccount} disabled={saving}>
                  {saving ? text.saving : text.save}
                </button>
              </section>
            )}

            {tab === 'password' && (
              <section className="sg-card">
                <h2>{text.passwordTitle}</h2>
                <label>{text.currentPassword}</label>
                <input type="password" value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} />
                <label>{text.newPassword}</label>
                <input type="password" value={pwd.newPwd} onChange={e => setPwd({ ...pwd, newPwd: e.target.value })} />
                <label>{text.confirmPassword}</label>
                <input type="password" value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} />
                <button className="btn btn-primary" onClick={savePwd} disabled={saving}>
                  {saving ? text.updating : text.updatePassword}
                </button>
              </section>
            )}

            {tab === 'notif' && (
              <section className="sg-card">
                <h2>{text.notifTitle}</h2>
                <div className="sg-toggle-row">
                  <div>
                    <div className="sg-toggle-label">{text.notificationLanguage}</div>
                    <div className="sg-toggle-desc">{text.notificationLanguageDesc}</div>
                  </div>
                  <select value={notificationLocale} onChange={e => setNotificationLocale(e.target.value)}>
                    <option value="en">{text.notificationLanguageOptions.en}</option>
                    <option value="zh">{text.notificationLanguageOptions.zh}</option>
                  </select>
                </div>
                {text.prefs.map(p => (
                  <div key={p.key} className="sg-toggle-row">
                    <div>
                      <div className="sg-toggle-label">{p.label}</div>
                      <div className="sg-toggle-desc">{p.desc}</div>
                    </div>
                    <label className="sg-switch">
                      <input type="checkbox" checked={prefs[p.key]} onChange={e => setPrefs({ ...prefs, [p.key]: e.target.checked })} />
                      <span className="sg-slider"></span>
                    </label>
                  </div>
                ))}
                <button className="btn btn-primary" onClick={savePrefs} disabled={saving}>
                  {saving ? text.saving : text.savePrefs}
                </button>
              </section>
            )}

            {tab === 'danger' && (
              <section className="sg-card">
                <h2>{text.dangerTitle}</h2>
                <div className="sg-danger">
                  <div className="sg-toggle-label">{text.clearTitle}</div>
                  <div className="sg-toggle-desc">{text.clearDesc}</div>
                  <button className="btn btn-danger" onClick={() => setClearConfirmVisible(true)}>{text.clearNow}</button>
                  {clearConfirmVisible && (
                    <div className="sg-clear-confirm" role="status">
                      <span>{text.clearConfirm}</span>
                      <button type="button" className="btn btn-danger btn-sm" onClick={clearData}>{text.confirmClear}</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setClearConfirmVisible(false)}>{text.cancel}</button>
                    </div>
                  )}
                </div>
              </section>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
